import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Subscription } from 'rxjs/Subscription'
import { UserModel } from '../../../models/user.model'
import { UserService } from '../../../services/user.service'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { FuseTranslationLoaderService } from '../../../../core/services/translation-loader.service'
import { userDutch } from '../i18n/nl'
import { userEnglish } from '../i18n/en'
import { TellowConstants } from '../../enums/TellowConstants'
import { MatDialog, MatSnackBar } from '@angular/material'
import { TranslateService } from '@ngx-translate/core'
import { Router } from '@angular/router'
import { RetrievedLoginCredentialsDialogComponent } from '../retrieved-login-credentials-dialog/retrieved-login-credentials-dialog.component'
import { LoginMethods } from '../../../models/login-methods.model'

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  // animations: fuseAnimations,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  user: UserModel
  onUserChanged: Subscription
  onSearchChanged: Subscription
  searchInput: FormControl
  userForm: FormGroup
  states = TellowConstants.getStatus()
  message: string
  currentUser = true

  @ViewChild('titleInput') titleInputField
  view = 'details'

  constructor(
    private userService: UserService,
    private formBuilder: FormBuilder,
    private translationLoader: FuseTranslationLoaderService,
    public snackBar: MatSnackBar,
    private translateService: TranslateService,
    private _router: Router,
    private dialog: MatDialog,
  ) {
    this.translationLoader.loadTranslations(userDutch, userEnglish)
    this.searchInput = new FormControl('')
  }

  ngOnInit() {
    // Subscribe to update the current administration
    this.onUserChanged = this.userService.onUserChanged.subscribe(user => {
      if (user != undefined) {
        this.user = new UserModel(user)
        this.userForm = this.createUserForm()
        this.focusTitleField()
        this.userForm.disable()
      }
    })

    this.onSearchChanged = this.searchInput.valueChanges
      .debounceTime(400)
      .distinctUntilChanged()
      .subscribe(searchText => {
        this.userService.changeSearch(searchText)
      })
  }

  public changeView(view: any): void {
    this.view = view
  }

  focusTitleField() {
    setTimeout(() => {
      this.titleInputField.nativeElement.focus()
    })
  }

  createUserForm() {
    return this.formBuilder.group({
      id: [this.user.id],
      first_name: [this.user.first_name, [Validators.required]],
      middle_name: [this.user.middle_name],
      last_name: [this.user.last_name, [Validators.required]],
      phone_number: [this.user.phone_number, [Validators.required]],
      email: [this.user.email, [Validators.required]],
      role: [this.user.role],
      status: [this.user.status],
    })
  }

  /**
   * Update a User
   */
  updateUser() {
    this.user.phone_number = this.userForm.get('phone_number').value
    this.user.first_name = this.userForm.get('first_name').value
    this.user.middle_name = this.userForm.get('middle_name').value
    this.user.last_name = this.userForm.get('last_name').value
    this.user.email = this.userForm.get('email').value
    this.user.status = this.userForm.get('status').value
    this.user.role = this.userForm.get('role').value

    this.userService.update(this.user).subscribe(
      response => {
        this.translateService.get('USER.MODIFIED', {}).subscribe((message: string) => {
          this.openSnackBar(message, 'X')
        })
      },
      error => {},
    )
  }

  /**
   *
   * @param $event
   */
  unSetCurrentUser($event) {
    this._router.navigate(['users'])
  }

  /**
   * Block a User
   * @param {UserModel} user
   */
  blockUser() {
    if (this.user.status == 2) {
      this.user.status = 3
      this.message = 'USER.BLOCKED'
    } else {
      this.user.status = 2
      this.message = 'USER.UNBLOCKED'
    }

    this.userService.block(this.user).subscribe(data => {
      this.translateService.get(this.message, {}).subscribe((message: string) => {
        this.openSnackBar(message, 'send')
      })
      this.openSnackBar('Gebruiker geblokkerd', 'X')
    })
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 3000,
    })
  }

  closeSnackBar() {
    this.snackBar.dismiss()
  }

  /**
   *
   * @param event
   */
  resendActivation(event) {
    event.stopPropagation()
    this.userService.resendActivation(this.user).subscribe(data => {
      this.translateService.get('USER.RESEND_SUCCESS', {}).subscribe((message: string) => {
        this.openSnackBar(message, 'X')
      })
    })
  }

  /**
   *
   * @param event
   */
  resendInvite(event) {
    event.stopPropagation()
    this.userService.resendInvite(this.user).subscribe(
      data => {
        this.translateService.get('USER.RESEND_SUCCESS', {}).subscribe((message: string) => {
          this.openSnackBar(message, 'X')
        })
      },
      error => {
        this.translateService.get('USER.ERROR_' + error.error.error_code, {}).subscribe((message: string) => {
          this.openSnackBar(message, 'X')
        })
      },
    )
  }

  /**
   * Retrieves login methods and shows them in a dialog
   * @param event
   */
  retrieveLoginMethods(event) {
    event.stopPropagation()
    this.translateService.get('USER.RETRIEVING_LOGIN_CREDENTIALS').subscribe((message: string) => {
      this.openSnackBar(message, 'X')
    })

    this.userService.retrieveInlogLinkAndPassword(this.user).subscribe((data: LoginMethods) => {
      this.closeSnackBar()

      const dialogRef = this.dialog.open(RetrievedLoginCredentialsDialogComponent, {
        hasBackdrop: true,
        data: {
          user: this.user,
          loginMethods: { magic_link: `${data.magic_link}?820b3818-92372b182ee1=cc6a394a`, one_time_password: data.one_time_password },
        },
      })
    })
  }

  /**
   *
   * @param event
   */
  passwordReset(event) {
    event.stopPropagation()
    this.userService.passwordReset(this.user).subscribe(data => {
      this.translateService.get('USER.SEND_PASSWORD_RESET_SUCCESS', {}).subscribe((message: string) => {
        this.openSnackBar(message, 'X')
      })
    })
  }

  /**
   *
   * @param event
   */
  deleteUser(event) {
    event.stopPropagation()
  }

  /**
   *
   * @returns {boolean}
   */
  checkBlocked() {
    if (this.user.status === 2 || this.user.status === 3) {
      return true
    }
    return false
  }

  /**
   *
   * @param event
   */
  toggleEdit(event) {
    if (this.userForm.disabled) {
      this.userForm.enable()
    } else {
      this.userForm.disable()
    }
  }

  /**
   *
   */
  ngOnDestroy() {
    this.onUserChanged.unsubscribe()
    this.onSearchChanged.unsubscribe()
  }
}



// WEBPACK FOOTER //
// ./src/app/main/content/users/user-details/user-details.component.ts